import React from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Link,
  Button,
  Typography,
} from '@mui/material';
import StatusBadge from './StatusBadge';
import CategoryBadge from './CategoryBadge';

function formatPrice(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

/** Categories that typically have floor/tower; others (plot, warehouse, etc.) show — or block/sector. */
const FLOOR_TOWER_CATEGORIES = ['residential', 'commercial', 'office', 'retail'];
function getCategoryKey(cat) {
  if (!cat) return 'other';
  return String(cat).toLowerCase().trim().replace(/\s+/g, '_').replace(/-/g, '');
}

function getFloorTowerDisplay(unit) {
  const key = getCategoryKey(unit.category);
  const hasFloorTower = FLOOR_TOWER_CATEGORIES.includes(key);
  if (hasFloorTower) {
    const block = unit.unit_block ?? unit.tower;
    const joined = [unit.floor != null ? `Floor ${unit.floor}` : null, block].filter(Boolean).join(' · ');
    return joined || '—';
  }
  // Plot, industrial, warehouse, other: show block/sector/plot_number if present, else —
  return unit.unit_block ?? unit.block ?? unit.sector ?? unit.plot_number ?? '—';
}

function UnitRow({
  unit,
  selected,
  onSelect,
  onProjectClick,
  onConfigClick,
  onBook,
  onView,
}) {
  const canBook = unit.status === 'available' || unit.status === 'hold';
  const floorTowerDisplay = getFloorTowerDisplay(unit);
  const areaStr = unit.area != null ? `${Number(unit.area).toLocaleString('en-IN')} ${unit.areaUnit || 'sqft'}` : '—';

  return (
    <TableRow
      hover
      sx={{
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          size="small"
          color="primary"
        />
      </TableCell>
      <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{unit.unitId}</TableCell>
      <TableCell>
        {onProjectClick && unit.projectId ? (
          <Link
            component="button"
            variant="body2"
            fontWeight={600}
            onClick={() => onProjectClick(unit)}
            sx={{ textAlign: 'left', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {unit.project}
          </Link>
        ) : (
          <Typography variant="body2" fontWeight={600}>{unit.project}</Typography>
        )}
      </TableCell>
      <TableCell>
        {onConfigClick && unit.configurationId ? (
          <Link
            component="button"
            variant="body2"
            fontWeight={600}
            onClick={() => onConfigClick(unit)}
            sx={{ textAlign: 'left', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {unit.configuration}
          </Link>
        ) : (
          <Typography variant="body2" fontWeight={600}>{unit.configuration}</Typography>
        )}
      </TableCell>
      <TableCell>
        <CategoryBadge category={unit.category} />
      </TableCell>
      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{floorTowerDisplay}</TableCell>
      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{areaStr}</TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
        {formatPrice(unit.price)}
      </TableCell>
      <TableCell>
        <StatusBadge status={unit.status} />
      </TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>
        {unit.channelPartner || '—'}
      </TableCell>
      <TableCell>
        {canBook && onBook && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onBook(unit)}
            sx={{
              bgcolor: 'success.main',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'success.dark' },
            }}
          >
            Book
          </Button>
        )}
        {!canBook && onView && (
          <Button
            size="small"
            variant="text"
            onClick={() => onView(unit)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            View
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

export default UnitRow;
